// Shared Zen building blocks used by more than one page.
import { CheckOutlined, FileOutlined, PrinterOutlined, UploadOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Checkbox, Flex, Input, Modal, Space, Tag, Typography, Upload } from 'antd';
import { useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '../lib/toast';
import * as api from './api';
import { formatDateTime } from './format';
import { useWorkOrder, zenKeys } from './hooks';
import { PaperWorkOrderSheet } from './PaperWorkOrderSheet';
import { isLogisticsTask, orderStatus, taskTimeInState } from './status';
import type { Comment, Department, DepartmentTask, Requirement, WorkOrder } from './types';
import { DueText, EmptyState, OrderNumber, PageSkeleton, StatusPill } from './ui';

const { Text, Paragraph } = Typography;

export type TaskVerb = 'start' | 'finish';

function requirementLabels(order: WorkOrder): string[] {
  return [
    order.installation.required ? 'Installation/Delivery' : '',
    order.repair.required ? 'Repair' : '',
  ].filter(Boolean);
}

function itemLabel(order: WorkOrder, task: DepartmentTask): string {
  const index = order.items.findIndex((item) => item.id === task.workOrderItemId);
  const item = index >= 0 && order.items.length > 1 ? `Item ${index + 1} · ` : '';
  return `${item}${task.departmentName}`;
}

export function ZenListRow({
  leading,
  title,
  description,
  actions,
  onClick,
}: {
  leading?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  onClick?: () => void;
}) {
  const content = (
    <>
      {leading && <span className="zen-list-leading">{leading}</span>}
      <span className="zen-list-copy">
        <span className="zen-list-title">{title}</span>
        {description && <span className="zen-list-description">{description}</span>}
      </span>
    </>
  );

  return (
    <div className="zen-list-row" role="listitem">
      {onClick
        ? <button type="button" className="zen-list-main zen-list-main--interactive" onClick={onClick}>{content}</button>
        : <div className="zen-list-main">{content}</div>}
      {actions && <div className="zen-list-actions">{actions}</div>}
    </div>
  );
}

export function CommentRows({ comments }: { comments: Comment[] }) {
  if (!comments.length) {
    return <EmptyState compact title="No notes yet" description="Notes added here are visible to everyone working on this order." />;
  }

  return (
    <div className="zen-list" role="list">
      {comments.map((item) => (
        <ZenListRow
          key={item.id}
          title={<Space size={8}><Text strong>{item.userName}</Text><Text type="secondary" className="zen-list-time">{formatDateTime(item.createdAt)}</Text></Space>}
          description={<Paragraph style={{ whiteSpace: 'pre-line', margin: 0 }}>{item.text}</Paragraph>}
        />
      ))}
    </div>
  );
}

export function Field({ label, children, wide = false, required = false, hint }: { label: string; children: ReactNode; wide?: boolean; required?: boolean; hint?: ReactNode }) {
  return (
    <div className={`form-group${wide ? ' full' : ''}`}>
      <label>{label}{required && <span className="ua-required" aria-hidden="true"> *</span>}</label>
      {children}
      {hint && <span className="ua-field-hint">{hint}</span>}
    </div>
  );
}

export function RequirementField({ label, value, onChange, disabled = false }: { label: string; value: Requirement; onChange: (value: Requirement) => void; disabled?: boolean }) {
  return (
    <Space orientation="vertical" size={8} style={{ width: '100%' }}>
      <Checkbox disabled={disabled} checked={value.required} onChange={(event) => onChange({ required: event.target.checked, details: event.target.checked ? value.details : '' })}>{label}</Checkbox>
      {value.required && <Input.TextArea disabled={disabled} rows={2} value={value.details} onChange={(event) => onChange({ ...value, details: event.target.value })} placeholder={`${label} details (optional)`} />}
    </Space>
  );
}

export function AttachmentUploadButton({ workOrderId, label }: { workOrderId: string; label?: string }) {
  const queryClient = useQueryClient();
  const [uploadCount, setUploadCount] = useState(0);
  return (
    <Upload
      multiple
      showUploadList={false}
      customRequest={async ({ file, onSuccess, onError }) => {
        setUploadCount((count) => count + 1);
        try {
          await api.uploadFile(workOrderId, file as File);
          await queryClient.invalidateQueries({ queryKey: zenKeys.order(workOrderId) });
          toast.success(`Attached ${(file as File).name}`);
          onSuccess?.(null);
        } catch (error) {
          toast.error(error instanceof Error ? error.message : 'Couldn’t attach the file. Try again.');
          onError?.(error as Error);
        } finally {
          setUploadCount((count) => count - 1);
        }
      }}
    >
      <Button
        aria-label={label ?? 'Attach file'}
        title={label ?? 'Attach file'}
        icon={<UploadOutlined />}
        loading={uploadCount > 0}
      >
        {label}
      </Button>
    </Upload>
  );
}

export function TaskCard({
  task,
  order,
  busy,
  onOpen,
  onAction,
  showDepartment = true,
  readOnly = false,
  showUpload = false,
}: {
  task: DepartmentTask;
  order: WorkOrder;
  busy?: boolean;
  onOpen: () => void;
  onAction?: (task: DepartmentTask, action: TaskVerb) => void;
  showDepartment?: boolean;
  readOnly?: boolean;
  showUpload?: boolean;
}) {
  const requirements = requirementLabels(order);
  const started = task.status === 'started';
  const done = task.status === 'completed';
  const timeInState = taskTimeInState(task);
  return (
    <Card className={`zen-task-card${started ? ' is-started' : ''}`} size="small">
      <button type="button" className="zen-task-card-open" onClick={onOpen} aria-label={`Open ${order.orderNumber} for ${order.customerName}`}>
        <span className="zen-task-card-meta">
          <OrderNumber>{order.orderNumber}</OrderNumber>
          <DueText date={order.deliveryDate} done={done} />
        </span>
        <span className="zen-task-card-client">{order.customerName}</span>
        {showDepartment && <span className="zen-task-card-dept">{itemLabel(order, task)}</span>}
        <span className="zen-task-card-desc">{task.itemDescription}</span>
        <span className="zen-task-card-foot">
          <span className="zen-task-card-tags">
            {requirements.map((label) => <Tag key={label}>{label}</Tag>)}
          </span>
          {timeInState && <span className="zen-task-card-time">{timeInState}</span>}
        </span>
        {!done && task.otherItemsIncomplete && isLogisticsTask(task) && (
          <span className="zen-task-card-warning">Other items are still in production. Start only for an intentional partial handover.</span>
        )}
      </button>
      {!readOnly && (onAction || showUpload) && (
        <Flex className="zen-task-card-actions" gap={8} align="center">
          {showUpload && <AttachmentUploadButton workOrderId={order.id} />}
          {onAction && (
            <Button
              block
              type={task.status === 'pending' ? 'primary' : 'default'}
              loading={busy}
              icon={task.status === 'pending' ? undefined : <CheckOutlined />}
              onClick={() => onAction(task, task.status === 'pending' ? 'start' : 'finish')}
            >
              {task.status === 'pending' ? 'Start' : 'Finish'}
            </Button>
          )}
        </Flex>
      )}
    </Card>
  );
}

export function WorkerTaskModal({
  task,
  order,
  departments,
  canUpload,
  busy,
  onAction,
  onClose,
}: {
  task: DepartmentTask | null;
  order: WorkOrder | null;
  departments: Department[];
  canUpload: boolean;
  busy?: boolean;
  onAction?: (task: DepartmentTask, action: TaskVerb) => void;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [comment, setComment] = useState('');
  const { data, isLoading } = useWorkOrder(order?.id ?? '');
  const detail = data?.workOrder ?? order;
  const action: TaskVerb = task?.status === 'pending' ? 'start' : 'finish';
  const isCancelled = detail?.status === 'cancelled';
  const canCollaborate = task?.status !== 'completed' && !isCancelled;

  const submitComment = async () => {
    if (!order || !comment.trim()) return;
    try {
      await api.addComment(order.id, comment);
      setComment('');
      await queryClient.invalidateQueries({ queryKey: zenKeys.order(order.id) });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Couldn’t add the note. Try again.');
    }
  };

  return (
    <Modal
      className="zen-worker-modal zen-responsive-modal"
      open={Boolean(order && task)}
      onCancel={onClose}
      width={860}
      title={order && task ? (
        <Flex gap={10} align="center" wrap>
          <OrderNumber>{order.orderNumber}</OrderNumber>
          <span>{order.customerName}</span>
          <StatusPill status={orderStatus(detail ?? order)} />
        </Flex>
      ) : null}
      footer={
        <Flex justify="space-between" align="center" gap={12} wrap className="zen-worker-modal-footer">
          <Button icon={<PrinterOutlined />} onClick={() => window.print()}>Print work order</Button>
          <Space wrap>
            <Button onClick={onClose}>Close</Button>
            {task && onAction && task.status !== 'completed' && !isCancelled && (
              <Button
                type={action === 'start' ? 'primary' : 'default'}
                icon={action === 'start' ? undefined : <CheckOutlined />}
                loading={busy}
                onClick={() => onAction(task, action)}
              >
                {action === 'start' ? 'Start' : 'Finish'}
              </Button>
            )}
          </Space>
        </Flex>
      }
    >
      {isLoading && <PageSkeleton />}
      {!isLoading && detail && order && task && (
        <Space orientation="vertical" size={16} style={{ width: '100%', paddingTop: 8 }}>
          {isCancelled && (
            <Alert
              type="error"
              showIcon
              title="This work order was cancelled"
              description="It is permanently read-only. Details, notes and files stay here for reference."
            />
          )}
          {task.status !== 'completed' && task.otherItemsIncomplete && isLogisticsTask(task) && (
            <Alert type="warning" showIcon title="Other items are still in production" description="This item can go out on its own. Start only if a partial delivery or installation is intended." />
          )}

          <PaperWorkOrderSheet order={detail} task={task} departments={departments} />

          <Card
            size="small"
            title="Files"
            extra={canCollaborate && canUpload ? <AttachmentUploadButton workOrderId={order.id} label="Attach" /> : null}
          >
            <Space size={8} wrap>
              {data?.attachments.map((file) => (
                <Button
                  key={file.id}
                  className="zen-file-button"
                  icon={<FileOutlined />}
                  onClick={() => api.downloadFile(order.id, file.id, file.name)}
                >
                  {file.name}
                </Button>
              ))}
              {!data?.attachments.length && <Text type="secondary">No files attached yet.</Text>}
            </Space>
          </Card>

          <Card size="small" title="Notes">
            <Space orientation="vertical" size={12} style={{ width: '100%' }}>
              <CommentRows comments={data?.comments ?? []} />
              {canCollaborate && (
                <Flex className="zen-comment-composer" gap={8} align="flex-start">
                  <Input.TextArea
                    value={comment}
                    onChange={(event) => setComment(event.target.value)}
                    onPressEnter={(event) => {
                      if (!event.shiftKey) {
                        event.preventDefault();
                        void submitComment();
                      }
                    }}
                    rows={2}
                    placeholder="Add a note for everyone working on this order"
                  />
                  <Button disabled={!comment.trim()} onClick={submitComment}>Add note</Button>
                </Flex>
              )}
            </Space>
          </Card>
        </Space>
      )}
    </Modal>
  );
}
